/**
 * useSiteNotes — React Query hooks for the Wave 4 / M1 multi-note site_notes
 * surface. Mirrors useSiteComments: cache keys live under
 * `siteKeys.detail(siteId)` so invalidating a site detail cascades to its
 * notes list.
 *
 * The list query keeps `pinned DESC, position ASC, created_at ASC` ordering
 * (matching the backend) so consumers can render the result directly without
 * re-sorting. Mutations optimistically merge into the cached list and then
 * invalidate the parent site detail to keep any embedded counts fresh.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { siteNotes } from '../api/methods';
import { siteKeys } from './useSites';
export const siteNoteKeys = {
    all: ['siteNotes'],
    list: (siteId, options = {}) => [
        ...siteKeys.detail(String(siteId)),
        'notes',
        { includeArchived: Boolean(options.includeArchived) },
    ],
    versions: (siteId, noteId) => [...siteKeys.detail(String(siteId)), 'notes', noteId, 'versions'],
};
/**
 * Server orders notes by `pinned DESC, position ASC, created_at ASC`. We
 * re-apply that here as a defensive sort so any optimistic insert that lands
 * out of order still renders correctly until the next refetch.
 */
function sortNotes(rows) {
    return [...rows].sort((a, b) => {
        if (a.pinned !== b.pinned)
            return a.pinned ? -1 : 1;
        if (a.position !== b.position)
            return a.position - b.position;
        const aTs = a.created_at ? Date.parse(a.created_at) : 0;
        const bTs = b.created_at ? Date.parse(b.created_at) : 0;
        return aTs - bTs;
    });
}
export function useSiteNotes(siteId, options = {}) {
    return useQuery({
        queryKey: siteNoteKeys.list(siteId, options),
        queryFn: async () => {
            const rows = await siteNotes.list(siteId, options);
            return sortNotes(rows);
        },
        enabled: siteId !== '' && siteId != null,
    });
}
export function useCreateSiteNote(siteId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (vars) => siteNotes.create(siteId, vars),
        onSuccess: (newNote) => {
            // Merge into both filter variants of the list cache (with and without
            // archived) so callers that have either toggle open stay in sync.
            queryClient.setQueriesData({ queryKey: [...siteKeys.detail(String(siteId)), 'notes'] }, (old) => sortNotes([...(old || []), newNote]));
            queryClient.invalidateQueries({
                queryKey: siteKeys.detail(String(siteId)),
            });
        },
    });
}
export function useUpdateSiteNote(siteId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ noteId, ...patch }) => siteNotes.update(siteId, noteId, patch),
        onSuccess: (updated) => {
            queryClient.setQueriesData({ queryKey: [...siteKeys.detail(String(siteId)), 'notes'] }, (old) => sortNotes((old || []).map((n) => (n.id === updated.id ? updated : n))));
            queryClient.invalidateQueries({
                queryKey: siteNoteKeys.versions(siteId, updated.id),
            });
        },
    });
}
export function useArchiveSiteNote(siteId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (noteId) => siteNotes.archive(siteId, noteId),
        onSuccess: (archived) => {
            // Drop archived rows from the active-only cache; refetches will populate
            // includeArchived=true variants on demand.
            queryClient.setQueriesData({ queryKey: [...siteKeys.detail(String(siteId)), 'notes'] }, (old) => (old || []).filter((n) => n.id !== archived.id));
            queryClient.invalidateQueries({
                queryKey: siteKeys.detail(String(siteId)),
            });
        },
    });
}
export function useDeleteSiteNote(siteId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (noteId) => siteNotes.remove(siteId, noteId),
        onSuccess: (_data, noteId) => {
            queryClient.setQueriesData({ queryKey: [...siteKeys.detail(String(siteId)), 'notes'] }, (old) => (old || []).filter((n) => n.id !== noteId));
            queryClient.invalidateQueries({
                queryKey: siteKeys.detail(String(siteId)),
            });
        },
    });
}
export function useReorderSiteNotes(siteId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (items) => siteNotes.reorder(siteId, items),
        onSuccess: () => {
            // Reorder returns only a count — refetch to pick up the canonical order.
            queryClient.invalidateQueries({
                queryKey: [...siteKeys.detail(String(siteId)), 'notes'],
            });
        },
    });
}
//# sourceMappingURL=useSiteNotes.js.map